import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { clearJobMatchProBrowserData } from '../lib/jobMatchStorage';
import { authCallbackUrl } from '../lib/pendingIntent';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentUserIdRef = useRef(null);

  const applySession = (nextSession) => {
    const nextUser = nextSession?.user ?? null;
    const nextUserId = nextUser?.id || null;
    const previousUserId = currentUserIdRef.current;
    if (previousUserId && nextUserId && previousUserId !== nextUserId) {
      clearJobMatchProBrowserData({ userId: previousUserId, includeSession: true });
    }
    if (previousUserId && !nextUserId) {
      clearJobMatchProBrowserData({ userId: previousUserId, includeSession: true });
    }
    currentUserIdRef.current = nextUserId;
    setSession(nextSession);
    setUser(nextUser);
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      applySession(session);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      applySession(session);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithOtp = async (email, redirectTo) => {
    const options = { shouldCreateUser: true };
    if (redirectTo) {
      options.emailRedirectTo = redirectTo;
    }
    return await supabase.auth.signInWithOtp({ email, options });
  };

  const verifyOtp = async (email, token) => {
    return await supabase.auth.verifyOtp({ email, token, type: 'email' });
  };

  const signInWithGoogle = async (redirectTo) => {
    return await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectTo || authCallbackUrl(),
      },
    });
  };

  const signOut = async () => {
    const departingUserId = currentUserIdRef.current || user?.id;
    const result = await supabase.auth.signOut();
    if (!result?.error) {
      clearJobMatchProBrowserData({ userId: departingUserId, includeSession: true });
      currentUserIdRef.current = null;
      setSession(null);
      setUser(null);
    }
    return result;
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signInWithOtp, verifyOtp, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
